import { INTERVAL_SECONDS } from '../config/timeframes.js';
import type {
  BreakthroughAssessment,
  Candle,
  CandidateDirection,
  CandidateEvidence,
  CandidateFamily,
  CandidateRecord,
  CandidateState,
  ChartInterval,
  DeepValidationStatus,
  Horizon,
  StructuralZone,
} from '../types/market.js';
import { getCandleTiming } from './candleTime.js';
import { studyCurrentCandle } from './candleStudy.js';
import { assessHistoricalBreakthrough } from './historicalBreakthrough.js';
import { relativeStrengthIndex } from './indicators.js';
import { clamp, percentChange } from './number.js';
import { detectStructuralZones } from './supportResistance.js';
import { assessTimeframe } from './timeframeAnalysis.js';

export interface DeepValidationMeta {
  status: Exclude<DeepValidationStatus, 'not-requested' | 'queued'>;
  coverage: number;
  issues: string[];
}

export interface DeepCandidateInput {
  candidate: CandidateRecord;
  candleMap: Partial<Record<ChartInterval, Candle[]>>;
  now: number;
  validation: DeepValidationMeta;
}

interface ContextPlan {
  primary: ChartInterval;
  secondary: ChartInterval;
  background?: ChartInterval;
}

const CONTEXT_PLAN: Record<Horizon, ContextPlan> = {
  '5m': { primary: '15m', secondary: '1h', background: '4h' },
  '15m': { primary: '1h', secondary: '4h', background: '1d' },
  '30m': { primary: '1h', secondary: '4h', background: '1d' },
  '1h': { primary: '4h', secondary: '1d', background: '1w' },
  '4h': { primary: '1d', secondary: '1w' },
};

export function deepCandidateIntervals(horizon: Horizon): ChartInterval[] {
  const plan = CONTEXT_PLAN[horizon];
  return [...new Set<ChartInterval>([horizon, plan.primary, plan.secondary, ...(plan.background ? [plan.background] : [])])];
}

function rsiDirection(candles: Candle[]): { current: number; previous: number } {
  const values = relativeStrengthIndex(candles.map((candle) => candle.close), 14);
  return {
    current: values.at(-1) ?? 50,
    previous: values.at(-4) ?? values.at(-1) ?? 50,
  };
}

function evidence(label: string, value: string, impact: CandidateEvidence['impact']): CandidateEvidence {
  return { label, value, impact };
}

function nearestZone(zones: StructuralZone[], kind: StructuralZone['kind']): StructuralZone | undefined {
  return zones
    .filter((zone) => zone.kind === kind)
    .sort((left, right) => Math.abs(left.distancePercent) - Math.abs(right.distancePercent))[0];
}

function zoneSummary(zone?: StructuralZone): CandidateRecord['nearestSupport'] {
  return zone
    ? {
        low: zone.low,
        high: zone.high,
        strength: zone.strength,
        distancePercent: zone.distancePercent,
      }
    : undefined;
}

function stateFromDeepScore(score: number): CandidateState {
  if (score >= 84) return 'strong';
  if (score >= 72) return 'confirming';
  if (score >= 58) return 'developing';
  if (score >= 43) return 'observing';
  return 'weakening';
}

function opposite(direction: CandidateDirection): CandidateDirection {
  return direction === 'long' ? 'short' : direction === 'short' ? 'long' : 'neutral';
}

function inferDeepThesis(
  candidate: CandidateRecord,
  selectedScore: number,
  activeBodyMove: number,
  breakthrough: BreakthroughAssessment,
): { direction: CandidateDirection; family: CandidateFamily; reason?: string } {
  if (candidate.direction !== 'neutral') return { direction: candidate.direction, family: candidate.family };

  const acceptedBreak = ['holding-beyond', 'closed-beyond', 'retest'].includes(breakthrough.state);
  if (acceptedBreak && breakthrough.direction !== 'neutral') {
    return {
      direction: breakthrough.direction,
      family: 'historical-breakthrough',
      reason: 'Deep chart history found an accepted adaptive historical breakthrough that the lightweight radar could not classify alone.',
    };
  }
  if (breakthrough.state === 'failed' && breakthrough.direction !== 'neutral') {
    return {
      direction: opposite(breakthrough.direction),
      family: 'historical-rejection',
      reason: 'Deep chart history found a failed historical breakthrough and a possible reversal away from the rejected boundary.',
    };
  }

  const bodyDirection: CandidateDirection = activeBodyMove > 0 ? 'long' : activeBodyMove < 0 ? 'short' : 'neutral';
  const structureDirection: CandidateDirection = selectedScore >= 24 ? 'long' : selectedScore <= -24 ? 'short' : 'neutral';
  if (structureDirection !== 'neutral' && bodyDirection === structureDirection) {
    return {
      direction: structureDirection,
      family: 'pullback-continuation',
      reason: 'Selected-horizon structure and the developing candle agree strongly enough for a provisional deep-analysis candidate.',
    };
  }

  return { direction: 'neutral', family: 'unclear' };
}

function assessmentText(interval: ChartInterval, candles: Candle[], maturity: number): string {
  if (candles.length < 10) return `${interval} unavailable`;
  const assessment = assessTimeframe(interval, candles, maturity);
  return `${interval} ${assessment.state.replace('-', ' ')}`;
}

export function deeplyValidateCandidate(input: DeepCandidateInput): CandidateRecord {
  const { candidate, candleMap, now, validation } = input;
  const horizonCandles = candleMap[candidate.horizon] ?? [];
  if (horizonCandles.length < 24) {
    return {
      ...candidate,
      deepValidated: false,
      validationStatus: 'failed',
      validationCoverage: validation.coverage,
      validationIssues: [...validation.issues, 'Selected-horizon history has fewer than 24 candles.'],
      candidateScore: Math.min(candidate.candidateScore, 52),
      statusNote: 'Deep validation could not establish enough selected-horizon history. The observation remains visible but cannot enter the predictor table.',
      lastDeepValidatedAt: now,
      lastUpdatedAt: now,
    };
  }

  const interval = candidate.horizon as ChartInterval;
  const timing = getCandleTiming(interval, now);
  const selected = assessTimeframe(interval, horizonCandles, timing.maturity);
  const plan = CONTEXT_PLAN[candidate.horizon];

  const assessmentFor = (contextInterval: ChartInterval) => {
    const contextTiming = getCandleTiming(contextInterval, now);
    const candles = candleMap[contextInterval] ?? [];
    const assessment = assessTimeframe(contextInterval, candles, contextTiming.maturity);
    return { interval: contextInterval, timing: contextTiming, assessment, available: candles.length >= 10 };
  };

  const primary = assessmentFor(plan.primary);
  const secondary = assessmentFor(plan.secondary);
  const background = plan.background ? assessmentFor(plan.background) : undefined;
  const breakthrough = assessHistoricalBreakthrough(horizonCandles);
  const currentPrice = horizonCandles.at(-1)?.close ?? candidate.currentPrice;
  const zones = detectStructuralZones(horizonCandles, currentPrice, 10);
  const nearestSupport = nearestZone(zones, 'support');
  const nearestResistance = nearestZone(zones, 'resistance');
  const rsi = rsiDirection(horizonCandles);
  const candleStudy = studyCurrentCandle(horizonCandles, timing.maturity);
  const activeCandle = horizonCandles.at(-1);
  const activeBodyMove = activeCandle ? percentChange(activeCandle.close, activeCandle.open) : 0;
  const inferred = inferDeepThesis(candidate, selected.score, activeBodyMove, breakthrough);
  const direction = inferred.direction;
  const family = inferred.family;
  const promotedFromNeutral = candidate.direction === 'neutral' && direction !== 'neutral';
  const directionalFoundAt = promotedFromNeutral ? now : candidate.foundAt;
  const directionalFoundPrice = promotedFromNeutral ? currentPrice : candidate.foundPrice;
  const directionalFoundChange24h = promotedFromNeutral ? candidate.currentChange24h : candidate.foundChange24h;
  const directionalExpiresAt = promotedFromNeutral
    ? now + INTERVAL_SECONDS[candidate.horizon] * 1_000
    : candidate.expiresAt;
  const directionSign = direction === 'long' ? 1 : direction === 'short' ? -1 : 0;

  const selectedAlignment = selected.score * directionSign;
  const primaryPressure = primary.available ? primary.assessment.score * directionSign : 0;
  const secondaryPressure = secondary.available ? secondary.assessment.score * directionSign : 0;
  const backgroundPressure = background?.available ? background.assessment.score * directionSign : 0;
  const breakthroughAligned = breakthrough.direction === direction;
  const breakthroughOpposed = breakthrough.direction !== 'neutral' && breakthrough.direction !== direction;
  const breakthroughConfirmed = ['holding-beyond', 'closed-beyond', 'retest'].includes(breakthrough.state);
  const historicalFailureSupports = breakthrough.state === 'failed' && breakthrough.direction !== direction;
  const rsiSlope = (rsi.current - rsi.previous) * directionSign;
  const rsiExtremeAgainst = direction === 'long' ? rsi.current > 78 : direction === 'short' ? rsi.current < 22 : false;
  const opposingZone = direction === 'long' ? nearestResistance : nearestSupport;
  const nextZoneDistance = Math.abs(opposingZone?.distancePercent ?? 4);
  const zoneSpaceScore = clamp(nextZoneDistance * 10, 0, 24);
  const lateZonePenalty = nextZoneDistance < 0.35 ? 16 : nextZoneDistance < 0.7 ? 7 : 0;
  const currentCandleAligned = directionSign !== 0 && Math.sign(activeBodyMove) === directionSign;
  const priorSupportBroken = direction === 'long' && candidate.nearestSupport
    ? currentPrice < candidate.nearestSupport.low && selectedAlignment < -8
    : false;
  const priorResistanceBroken = direction === 'short' && candidate.nearestResistance
    ? currentPrice > candidate.nearestResistance.high && selectedAlignment < -8
    : false;
  const structuralInvalidation = candidate.state === 'invalidated' || priorSupportBroken || priorResistanceBroken;

  let adjustment = 0;
  adjustment += clamp(selectedAlignment * 0.22, -22, 22);
  adjustment += clamp(primaryPressure * 0.1, -10, 10);
  adjustment += clamp(secondaryPressure * 0.055, -6, 6);
  adjustment += clamp(backgroundPressure * 0.025, -3, 3);
  adjustment += zoneSpaceScore - 10;
  adjustment -= lateZonePenalty;
  adjustment += rsiSlope > 2 ? 5 : rsiSlope < -2 ? -5 : 0;
  adjustment -= rsiExtremeAgainst ? 6 : 0;
  adjustment += breakthroughAligned && breakthroughConfirmed ? 10 : 0;
  adjustment += historicalFailureSupports ? 6 : 0;
  adjustment -= breakthroughOpposed && breakthroughConfirmed ? 12 : 0;
  adjustment += currentCandleAligned ? 3 : -2;
  adjustment += candleStudy.volumeRatio >= 1.15 && currentCandleAligned ? 4 : 0;
  adjustment -= timing.freshIssuanceAllowed ? 0 : 14;
  adjustment -= validation.status === 'partial' ? Math.round((1 - validation.coverage) * 10) : 0;
  adjustment -= direction === 'neutral' ? 16 : 0;

  let deepScore = Math.round(clamp(candidate.candidateScore * 0.58 + 28 + adjustment, 0, 100));
  if (structuralInvalidation) deepScore = Math.min(deepScore, 32);
  if (direction === 'neutral') deepScore = Math.min(deepScore, 55);
  const continuationQuality = Math.round(clamp(candidate.continuationQuality * 0.55 + deepScore * 0.45, 0, 100));
  const reversalRisk = Math.round(clamp(100 - continuationQuality + candidate.lateEntryRisk * 0.25, 0, 100));
  const pressureImpact = (value: number): CandidateEvidence['impact'] =>
    value >= 20 ? 'supports' : value <= -20 ? 'opposes' : 'neutral';

  const contextEvidence = [primary, secondary, ...(background ? [background] : [])].map((item, index) =>
    evidence(
      `Deep: ${item.interval} ${index === 0 ? 'parent' : index === 1 ? 'wider' : 'background'} pressure`,
      item.available
        ? `${item.assessment.state.replace('-', ' ')} · ${item.timing.maturity.toFixed(0)}% mature`
        : 'History unavailable; this timeframe was not used to command the selected horizon.',
      item.available ? pressureImpact(item.assessment.score * directionSign) : 'neutral',
    ),
  );

  const evidenceItems: CandidateEvidence[] = [
    ...candidate.evidence.filter((item) => !item.label.startsWith('Deep:')),
    evidence(
      `Deep: ${candidate.horizon} structure`,
      `${selected.state.replace('-', ' ')} (${selected.score.toFixed(0)})`,
      selectedAlignment >= 15 ? 'supports' : selectedAlignment <= -15 ? 'opposes' : 'neutral',
    ),
    ...contextEvidence,
    evidence(
      'Deep: historical structure',
      `${breakthrough.state.replace('-', ' ')} · adaptive depth ${breakthrough.candlesSurpassed} candles · boundary strength ${breakthrough.boundaryStrength.toFixed(0)}`,
      breakthroughAligned && breakthroughConfirmed
        ? 'supports'
        : breakthroughOpposed && breakthroughConfirmed
          ? 'opposes'
          : 'neutral',
    ),
    evidence(
      'Deep: nearest opposing zone',
      opposingZone ? `${nextZoneDistance.toFixed(2)}% away · strength ${opposingZone.strength.toFixed(0)}` : 'No nearby opposing zone resolved',
      nextZoneDistance >= 1 ? 'supports' : nextZoneDistance < 0.35 ? 'opposes' : 'neutral',
    ),
    evidence(
      'Deep: contextual RSI',
      `${rsi.current.toFixed(1)} (${rsiSlope >= 0 ? 'improving' : 'weakening'} for this direction)`,
      rsiSlope > 2 && !rsiExtremeAgainst ? 'supports' : rsiSlope < -2 || rsiExtremeAgainst ? 'opposes' : 'neutral',
    ),
    evidence(
      'Deep: active candle timing',
      `${timing.maturity.toFixed(0)}% mature · ${timing.phase} · volume pace ${candleStudy.volumeRatio.toFixed(2)}×`,
      timing.freshIssuanceAllowed ? 'neutral' : 'opposes',
    ),
    evidence(
      'Deep: validation coverage',
      `${Math.round(validation.coverage * 100)}% of requested timeframes available${validation.issues.length ? ` · ${validation.issues.length} issue${validation.issues.length === 1 ? '' : 's'}` : ''}`,
      validation.status === 'complete' ? 'supports' : 'neutral',
    ),
  ];

  const state = structuralInvalidation ? 'invalidated' : stateFromDeepScore(deepScore);
  const statusNote = structuralInvalidation
    ? `The original ${direction} thesis is invalidated because price broke its monitored structural boundary with opposing selected-horizon pressure.`
    : direction === 'neutral'
      ? 'Deep chart history was available, but the evidence still does not establish a clear long or short candidate.'
      : !timing.freshIssuanceAllowed
        ? 'Deep analysis remains visible, but fresh same-horizon promotion is blocked near candle close.'
        : deepScore >= 58 && !candidate.baselineReady
          ? 'Early deep chart evidence supports a provisional candidate while the live rolling baseline is still maturing; confidence remains reduced until more live observations accumulate.'
          : deepScore >= 58
            ? `Deep timeframe, history, timing, and zone checks support a ${validation.status === 'partial' ? 'provisional' : 'qualified'} potential candidate.`
            : 'The market remains monitored because deep context has not established enough directional edge.';
  const currentChangeSinceFound = percentChange(currentPrice, directionalFoundPrice);
  const favorableMove = direction === 'short' ? -currentChangeSinceFound : direction === 'long' ? currentChangeSinceFound : 0;
  const contextParts = [
    assessmentText(primary.interval, candleMap[primary.interval] ?? [], primary.timing.maturity),
    assessmentText(secondary.interval, candleMap[secondary.interval] ?? [], secondary.timing.maturity),
    ...(background ? [assessmentText(background.interval, candleMap[background.interval] ?? [], background.timing.maturity)] : []),
  ];
  const deepDiscovery = promotedFromNeutral
    ? {
        direction,
        family,
        state,
        candidateScore: deepScore,
        opportunityFreshness: candidate.opportunityFreshness,
        movementStrength: candidate.movementStrength,
        directionalEvidence: candidate.directionalEvidence,
        continuationQuality,
        reversalRisk,
        lateEntryRisk: candidate.lateEntryRisk,
        dataQuality: candidate.dataQuality,
        primaryDriver: candidate.primaryDriver,
        reason: inferred.reason ?? candidate.reason,
        statusNote,
        candleMaturity: timing.maturity,
        secondsToClose: Math.floor(timing.remainingMs / 1_000),
        evidence: evidenceItems.map((item) => ({ ...item })),
        sourceMarkets: [...candidate.sourceMarkets],
      }
    : candidate.discovery;

  return {
    ...candidate,
    direction,
    family,
    originalDirection: promotedFromNeutral ? direction : candidate.originalDirection === 'neutral' ? direction : candidate.originalDirection,
    originalFamily: promotedFromNeutral ? family : candidate.originalFamily === 'unclear' ? family : candidate.originalFamily,
    reason: inferred.reason ?? candidate.reason,
    state,
    foundAt: directionalFoundAt,
    expiresAt: directionalExpiresAt,
    foundPrice: directionalFoundPrice,
    foundChange24h: directionalFoundChange24h,
    currentPrice,
    changeSinceFound: currentChangeSinceFound,
    bestMove: promotedFromNeutral ? favorableMove : Math.max(candidate.bestMove, favorableMove),
    worstMove: promotedFromNeutral ? favorableMove : Math.min(candidate.worstMove, favorableMove),
    continuationQuality,
    reversalRisk,
    candidateScore: deepScore,
    coreScore: deepScore,
    priorityScore: deepScore + (candidate.supportingScore ?? 0),
    statusNote,
    ...(structuralInvalidation
      ? { transitionReason: candidate.transitionReason ?? 'Previously monitored support/resistance failed; the original thesis is now invalidated.' }
      : promotedFromNeutral
        ? { transitionReason: `Deep chart history upgraded a neutral observation into a fresh ${direction} candidate at the current price; earlier neutral movement is not counted as directional performance.` }
        : {}),
    candleMaturity: timing.maturity,
    secondsToClose: Math.floor(timing.remainingMs / 1_000),
    freshIssuanceAllowed: timing.freshIssuanceAllowed,
    deepValidated: true,
    validationStatus: validation.status,
    validationCoverage: validation.coverage,
    validationIssues: validation.issues,
    lastDeepValidatedAt: now,
    ...(zoneSummary(nearestSupport) ? { nearestSupport: zoneSummary(nearestSupport) } : {}),
    ...(zoneSummary(nearestResistance) ? { nearestResistance: zoneSummary(nearestResistance) } : {}),
    contextSummary: `${contextParts.join('; ')}. Parent states are pressure, not commands.`,
    evidence: evidenceItems,
    ...(deepDiscovery ? { discovery: deepDiscovery } : {}),
    lastUpdatedAt: now,
  };
}
